// src/legal/LegalDocuments.tsx
//
// The way to the list of legal documents, for the "Legal" link next to
// Privacy and Imprint (components/FooterLinks). The dialog behind it is
// loaded when it is first opened.
import { lazy, Suspense, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { KEEP_DIALOG_SIZE } from '../root/touchTheme';

const LegalDocumentsDialog = lazy(() => import('./LegalDocumentsDialog'));

export function useLegalDocuments() {
    const [open, setOpen] = useState(false);
    return {
        legalDocumentsOpen: open,
        openLegalDocuments: () => setOpen(true),
        closeLegalDocuments: () => setOpen(false),
    };
}

export const LegalDocuments = ({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) =>
    open ? (
        // The lazy part can fail to arrive: a tab opened before a deployment
        // asks for a file that is gone. Without a boundary of its own that
        // error reaches react-admin's and replaces the whole app.
        <ErrorBoundary fallbackRender={() => <LoadFailed onClose={onClose} />}>
            <Suspense fallback={null}>
                <LegalDocumentsDialog onClose={onClose} />
            </Suspense>
        </ErrorBoundary>
    ) : null;

const LoadFailed = ({ onClose }: { onClose: () => void }) => (
    <Dialog
        open
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        className={KEEP_DIALOG_SIZE}
        aria-labelledby="legal-documents-failed"
    >
        <DialogTitle id="legal-documents-failed">Legal documents</DialogTitle>
        <DialogContent>
            <DialogContentText role="alert">
                The legal documents could not be loaded. Reload the page to try
                again.
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Close</Button>
            <Button
                variant="contained"
                onClick={() => window.location.reload()}
            >
                Reload
            </Button>
        </DialogActions>
    </Dialog>
);
